/**
 * useWorkspacePerms — effective permissions + role-based nav visibility for the
 * active workspace. Returns `can(code)`, `navOverrides` ({navKey: "show"|"hide"}),
 * `defaultRoute` (role landing page), and a loading flag.
 * Fail-open on transient errors (backend remains the source of truth).
 *
 * Feb 2026 · The hook is used by ~50 components (PermGate, AppShell, sidebar
 * items, page shells, etc). Prior to R318 each hook instance owned its own
 * `useEffect` — meaning one page render could install dozens of 60-second
 * poll intervals + focus listeners AND fire dozens of parallel fetches at
 * cold start. That amplified `GET /me/permissions/{ws}` into the "hammering"
 * pattern the prod RCA (Sep-23) flagged as the #1 source of pod CPU pressure.
 *
 * The hook is now a THIN subscriber over a module-scope singleton that owns
 * exactly one inflight fetch, one 60-second poll, and one focus listener per
 * workspace_id. Components mount/unmount freely; only the LAST unmount tears
 * down the timer + listener.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getInflightBootstrap } from "@/lib/useBootstrap";

// ---------- Singleton coordinator (module scope) ----------

const _cache = new Map();        // wsId → snapshot
const _inflight = new Map();     // wsId → Promise (dedupes concurrent fetches)
const _subscribers = new Map();  // wsId → Set<listener>
const _timers = new Map();       // wsId → intervalId
let _focusListenerCount = 0;     // ref-count so ONE listener serves all wsIds

function _shape(raw) {
  return {
    perms: new Set(raw?.permissions || []),
    navOverrides: raw?.nav_overrides || {},
    defaultRoute: raw?.default_route || null,
    isAdminRole: !!raw?.is_admin_role,
    isMember: !!raw?.is_member,
    modules: raw?.modules || {},
  };
}

function _notify(wsId) {
  const subs = _subscribers.get(wsId);
  if (!subs) return;
  const snap = _cache.get(wsId) || null;
  subs.forEach((fn) => { try { fn(snap); } catch { /* subscriber threw */ } });
}

function _fetch(wsId, { force = false } = {}) {
  if (!wsId) return Promise.resolve(null);
  if (!force && _cache.has(wsId)) return Promise.resolve(_cache.get(wsId));
  if (_inflight.has(wsId)) return _inflight.get(wsId);

  // Feb 2026 · R293 — If `useBootstrap` is currently fetching the
  // aggregate endpoint, await it (it will call `primeFromBootstrap`
  // on resolve). This dedupes the first-page-load race where both
  // hooks mount in the same tick and would otherwise fire TWO
  // network calls returning the same data. Any failure here falls
  // through to the standalone fetch below so the hook always
  // returns a shape.
  const bootstrapPrime = force ? null : getInflightBootstrap(wsId);

  const standalone = () => api.get(`/me/permissions/${wsId}`)
    .then((r) => {
      const snap = _shape(r.data);
      _cache.set(wsId, snap);
      _notify(wsId);
      return snap;
    })
    .catch(() => {
      if (!_cache.has(wsId)) _notify(wsId);
      return _cache.get(wsId) || null;
    });

  const p = bootstrapPrime
    ? bootstrapPrime
        .then(() => _cache.get(wsId) || standalone())
        .catch(() => standalone())
        .finally(() => { /* no-op; bootstrap handles its own inflight */ })
    : standalone();
  const wrapped = Promise.resolve(p).finally(() => { _inflight.delete(wsId); });
  _inflight.set(wsId, wrapped);
  return wrapped;
}

function _onFocus() {
  // Refresh EVERY workspace we're currently subscribed to — cheap, since
  // real users only ever have one active workspace, so this loops once.
  _subscribers.forEach((subs, wsId) => { if (subs.size) _fetch(wsId, { force: true }); });
}

function _subscribe(wsId, listener) {
  if (!wsId) return () => {};
  let subs = _subscribers.get(wsId);
  if (!subs) { subs = new Set(); _subscribers.set(wsId, subs); }
  subs.add(listener);

  // First subscriber for THIS workspace boots the 60-second poll.
  if (!_timers.has(wsId)) {
    const iv = setInterval(() => _fetch(wsId, { force: true }), 60_000);
    _timers.set(wsId, iv);
  }
  // First-ever subscriber (across all workspaces) installs the focus listener.
  if (_focusListenerCount === 0) window.addEventListener("focus", _onFocus);
  _focusListenerCount += 1;

  // Kick a fetch (uses cache if present, else network).
  _fetch(wsId, { force: false });

  return () => {
    const s = _subscribers.get(wsId);
    if (!s) return;
    s.delete(listener);
    if (s.size === 0) {
      _subscribers.delete(wsId);
      const iv = _timers.get(wsId);
      if (iv) { clearInterval(iv); _timers.delete(wsId); }
    }
    _focusListenerCount -= 1;
    if (_focusListenerCount <= 0) {
      _focusListenerCount = 0;
      window.removeEventListener("focus", _onFocus);
    }
  };
}

// Iter 253j · Exposed so `auth.jsx` can nuke the cache on workspace switch —
// preventing a limited-role user from seeing admin perms carried over from
// the previous workspace's fetch.
export function clearWorkspacePermsCache() {
  _cache.clear();
  _inflight.clear();
  // Also re-fetch anything that still has active subscribers so PermGates
  // in the newly-mounted layout land with fresh data.
  _subscribers.forEach((subs, wsId) => { if (subs.size) _fetch(wsId, { force: true }); });
}

// Feb 2026 · R293 · Called by `useBootstrap` after the aggregate
// `/workspaces/{ws}/bootstrap` call returns the permissions slice.
// Seeds the cache so the standalone `/me/permissions/{ws}` fetch
// becomes redundant on page load — removes one of the six per-page
// hits flagged in the 18:00 IST slowdown RCA. Any subsequent
// `_fetch(force: true)` (role change, focus) still goes through the
// standalone endpoint.
export function primeFromBootstrap(wsId, raw) {
  if (!wsId || !raw) return;
  const snap = _shape(raw);
  _cache.set(wsId, snap);
  _notify(wsId);
}

// ---------- Hook ----------

export function useWorkspacePerms() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(() => _cache.get(activeWorkspaceId) || null);
  const [ready, setReady] = useState(!!_cache.get(activeWorkspaceId));

  useEffect(() => {
    if (!activeWorkspaceId) { setData(null); setReady(false); return; }
    // Sync immediately from cache if available (mount-time flash prevention).
    const cached = _cache.get(activeWorkspaceId) || null;
    setData(cached);
    setReady(!!cached);
    const unsub = _subscribe(activeWorkspaceId, (snap) => {
      setData(snap);
      setReady(true);
    });
    return unsub;
  }, [activeWorkspaceId]);

  return useMemo(() => {
    // R138p-p3 · Coerce `data.perms` to a Set defensively.
    const permsSet = data?.perms instanceof Set
      ? data.perms
      : new Set(Array.isArray(data?.perms) ? data.perms : []);
    // Feb 2026 · Granular Finance-Hub perms act as "at least finance.view".
    const _FIN_GRANULAR_PREFIXES = [
      "finance.sales.", "finance.purchases.", "finance.reports.",
      "finance.compliance.",
    ];
    const _hasAnyGranularFinance = () => {
      for (const p of permsSet) {
        for (const pref of _FIN_GRANULAR_PREFIXES) {
          if (typeof p === "string" && p.startsWith(pref)) return true;
        }
      }
      return false;
    };
    const check = (code) => {
      if (!ready) return false;
      if (!data) return false;
      if (permsSet.has(code)) return true;
      if (code === "finance.view" || code === "finance.manage") {
        return _hasAnyGranularFinance();
      }
      if (code === "invoice.view" || code === "invoice.manage"
          || code === "payment.view" || code === "payment.manage") {
        return permsSet.has("finance.sales.quotes.view")
            || permsSet.has("finance.sales.quotes.manage")
            || permsSet.has("finance.sales.receivables.view")
            || permsSet.has("finance.sales.receivables.manage");
      }
      if (code === "expense.view" || code === "expense.manage") {
        return permsSet.has("finance.purchases.vendors.view")
            || permsSet.has("finance.purchases.vendors.manage")
            || permsSet.has("finance.purchases.expenses.view")
            || permsSet.has("finance.purchases.expenses.manage");
      }
      if (code === "report.view") {
        return permsSet.has("finance.reports.revenue_flow.view")
            || permsSet.has("finance.reports.financial.view")
            || permsSet.has("finance.reports.leadership.view");
      }
      return false;
    };
    return {
      ready,
      perms: permsSet,
      navOverrides: data?.navOverrides || {},
      defaultRoute: data?.defaultRoute || null,
      isAdminRole: !!data?.isAdminRole,
      isMember: !!data?.isMember,
      modules: {
        brands: data?.modules?.brands !== false,
        ...(data?.modules || {}),
      },
      can: check,
      hasPerm: check,
      refresh: () => {
        _cache.delete(activeWorkspaceId);
        _fetch(activeWorkspaceId, { force: true });
      },
    };
  }, [data, ready, activeWorkspaceId]);
}

export default useWorkspacePerms;
